
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { BikeUsageProgress } from './BikeUsageProgress';
import { useIsMobile } from '@/hooks/use-mobile';
import { 
  BarChart, 
  Users,
  BadgeDollarSign,
  Calendar,
  BarChart3,
  PieChart,
  TrendingUp
} from 'lucide-react';
import { fetchDashboardStats } from '@/services/analyticsService';

export const KeyMetricsPanel: React.FC = () => {
  const isMobile = useIsMobile();
  const [metricsData, setMetricsData] = useState({
    totalPayments: 0,
    currentMonthRevenue: 0, // Доход за текущий месяц
    activeRentals: 0,
    activeCouriers: 0,
    avgRentalAmount: 0,
    bikeUsage: { used: 0, total: 0 },
    revenueGrowth: 0 // Прирост дохода
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        console.log('Загрузка метрик с новой логикой активных сущностей...');
        const stats = await fetchDashboardStats();
        setMetricsData({
          totalPayments: stats.totalPayments,
          currentMonthRevenue: stats.currentMonthRevenue, // Используем доход за текущий месяц
          activeRentals: stats.activeRentals, // Новая логика: оплаченные аренды
          activeCouriers: stats.activeCouriers, // Новая логика: курьеры с арендами и платежами
          avgRentalAmount: stats.avgRentalAmount,
          bikeUsage: { 
            used: stats.activeBikes || 0, 
            total: stats.totalBikes || 0 
          },
          revenueGrowth: stats.revenueGrowth || 0 // Прирост дохода
        });
        console.log('Метрики обновлены с новой логикой:', {
          activeCouriers: stats.activeCouriers,
          activeRentals: stats.activeRentals
        });
      } catch (error) {
        console.error('Ошибка при загрузке метрик:', error);
      } finally {
        setLoading(false);
      }
    }

    loadData();

    // Обновляем данные каждые 5 минут
    const intervalId = setInterval(loadData, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);
  
  return (
    <Card className="border-border/40 bg-card/20 backdrop-blur-sm">
      <CardContent className={`p-4 ${isMobile ? 'py-3' : 'p-6'}`}>
        <div className="grid gap-4 md:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          <BikeUsageProgress 
            used={metricsData.bikeUsage.used} 
            total={metricsData.bikeUsage.total} 
            loading={loading} 
          />
          
          <Card className="revenue-metrics-card h-full">
            <CardContent className="p-4">
              <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold flex items-center mb-2`}>
                <BadgeDollarSign className="h-5 w-5 mr-2 text-tuktuk-teal" />
                Доход за месяц
              </h3>
              <div className="text-2xl font-bold">
                {loading ? (
                  <div className="h-6 bg-muted rounded animate-pulse w-2/3"></div>
                ) : (
                  `₽${new Intl.NumberFormat('ru-RU').format(metricsData.currentMonthRevenue)}`
                )}
              </div>
              <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground mt-1 flex items-center`}>
                <TrendingUp className={`h-3 w-3 mr-1 ${metricsData.revenueGrowth >= 0 ? 'text-green-500' : 'text-red-500'}`} />
                {loading ? (
                  <div className="h-3 bg-muted rounded animate-pulse w-24"></div>
                ) : (
                  `Прирост ${metricsData.revenueGrowth >= 0 ? '+' : ''}${metricsData.revenueGrowth.toFixed(1)}% к прошлому месяцу`
                )}
              </div>
            </CardContent>
          </Card>
          
          <Card className="couriers-metrics-card h-full">
            <CardContent className="p-4">
              <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold flex items-center mb-2`}>
                <Users className="h-5 w-5 mr-2 text-tuktuk-teal" />
                Оплатило Курьеров
              </h3>
              <div className="text-2xl font-bold">
                {loading ? (
                  <div className="h-6 bg-muted rounded animate-pulse w-1/3"></div>
                ) : (
                  metricsData.activeCouriers
                )}
              </div>
              <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground mt-1`}>
                С арендами и платежами
              </div>
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>
  );
};
